import React from 'react';
import intl from 'react-intl-universal';
import { Link } from 'react-router-dom';
import {
  Button,
  Icon,
  List,
  Popup,
  Typography,
  Tooltip,
  tailwindOverride,
  IconButton,
} from '@getsynapse/design-system';
import loadingIcon from 'assets/icons/loading.svg';
import ImportButton, { ImportButtonProps } from 'Molecules/ImportButton';
import { MoreActionsOption } from 'utils/customTypes';

export type OptionProps = Omit<Partial<MoreActionsOption>, 'selectOption'> & {
  isSectionTitle?: boolean;
  isImportButton?: boolean;
  loading?: boolean;
  tabName?: string;
  renderLink?: string;
  selectOption?: (callback: () => void) => void;
  importButtonProps?: Omit<
    ImportButtonProps,
    'disabled' | 'isListItemButton' | ' onLaunchCallback' | 'buttonText'
  >;
};

const ExportComponent: React.FC<{ option: OptionProps; link: string }> = ({
  option,
  link,
}) => {
  return (
    <a
      href={link}
      target='_blank'
      rel='noreferrer'
      className={tailwindOverride(
        'text-sm',
        { 'text-neutral-light cursor-not-allowed': option.disabled },
        {
          'text-primary group-hover:text-secondary-darker cursor-pointer':
            !option.disabled,
        },
      )}
    >
      {option.label}
    </a>
  );
};
const ListItemContent: React.FC<{ option: OptionProps }> = ({ option }) => {
  return (
    <div className='py-1 px-2 flex items-center'>
      {(option.iconSrc || option.iconName || option.loading) && (
        <Icon
          className={tailwindOverride(
            'fill-current text-neutral-black mr-2 text-xl',
            {
              'group-hover:fill-current group-hover:text-neutral-dark':
                !option.disabled,
              'fill-current text-neutral-light cursor-not-allowed':
                option.disabled,
              'animate-spin': option.loading,
            },
          )}
          name={option.loading ? '' : option.iconName || ''}
          src={option.loading ? loadingIcon : option.iconSrc || ''}
          aria-hidden={option.loading ? false : true}
          aria-label={option.loading ? intl.get('LOADING') : option.label}
        />
      )}
      {option.renderLink ? (
        <ExportComponent option={option} link={option.renderLink} />
      ) : (
        <Typography
          className={tailwindOverride(
            { 'text-neutral-light cursor-not-allowed': option.disabled },
            {
              'text-primary group-hover:text-secondary-darker cursor-pointer':
                !option.disabled,
            },
          )}
          variant='label'
        >
          {option.label}
        </Typography>
      )}
    </div>
  );
};

const ListItem: React.FC<{
  option: OptionProps;
  close: () => void;
}> = ({ option, close }) => {
  return option.isImportButton && option.importButtonProps ? (
    <ImportButton
      {...option.importButtonProps}
      isListItemButton
      onLaunchCallback={() => close()}
      buttonText={option.label}
      disabled={option.disabled}
    />
  ) : (
    <li
      onClick={() => {
        if (option.disabled !== true && option.selectOption) {
          option.selectOption(close);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          if (option.disabled !== true && option.selectOption) {
            option.selectOption(close);
          }
        }
      }}
      className={tailwindOverride(
        'font-body text-base truncate cursor-pointer group px-4 py-1 text-neutral-black',
        'focus-visible:border-0 focus-visible:ring-0 focus-visible:outline-none',
        {
          'hover:text-secondary-darker hover:shadow-list-item-hover-green hover:bg-secondary-lightest':
            !option.disabled,
          'focus-visible:bg-secondary-lightest focus-visible:text-secondary-darker focus-visible:shadow-list-item-hover-green':
            !option.disabled,
          'cursor-not-allowed': option.disabled,
        },
      )}
      data-cy={option.dataCy}
      role='option'
      tabIndex={0}
      aria-selected={false}
    >
      <ListItemContent option={option} />
    </li>
  );
};

const SectionTitle: React.FC<{ option: OptionProps }> = ({ option }) => {
  return (
    <li className='py-1 px-4'>
      <Typography variant='caption' weight='medium' className='text-neutral'>
        {option.label}
      </Typography>
    </li>
  );
};

const ActionItem: React.FC<{
  option: OptionProps;
  close: () => void;
}> = ({ option, close }) => {
  if (option.isSectionTitle) {
    return <SectionTitle option={option} />;
  }

  if (option.tooltip?.text) {
    return (
      <Tooltip
        position={
          option.tooltip?.position ? option.tooltip?.position : 'bottomLeft'
        }
        contentProps={{
          className: 'z-10',
        }}
        className='w-full'
        openMode='hover2'
        timeout={0}
        showPopper
        trigger={
          <div>
            <ListItem option={option} close={close} />
          </div>
        }
      >
        {option.tooltip?.text}
      </Tooltip>
    );
  }

  return <ListItem option={option} close={close} />;
};

const MoreActionsPopup: React.FC<{
  options: OptionProps[];
  buttonVariant?: 'tertiary' | 'primary';
  buttonText?: string;
  hasSplitButton?: boolean;
  linkUrl?: string;
  externalLinkUrl?: string;
  buttonTestId?: string;
  listClassName?: string;
  splitButtonOnClick?: () => void;
  splitButtonVariant?: 'tertiary' | 'primary';
}> = ({
  options,
  buttonVariant = 'tertiary',
  buttonText = intl.get('MORE_ACTIONS'),
  hasSplitButton = false,
  linkUrl = '',
  externalLinkUrl = '',
  buttonTestId = '',
  listClassName = '',
  splitButtonOnClick,
  splitButtonVariant = 'primary',
}) => {
  const splitButton = hasSplitButton && (
    <Button
      className='rounded-none rounded-l'
      variant={splitButtonVariant}
      data-testid={buttonTestId}
      onClick={splitButtonOnClick}
    >
      {buttonText}
    </Button>
  );

  return (
    <div className='flex'>
      {linkUrl && (
        <Link to={linkUrl} tabIndex={-1}>
          {splitButton}
        </Link>
      )}
      {!linkUrl && externalLinkUrl && (
        <a href={externalLinkUrl} tabIndex={-1}>
          {splitButton}
        </a>
      )}
      {!linkUrl && !externalLinkUrl && splitButton}
      <Popup
        position='bottom-end'
        popperProps={{ className: 'z-5' }}
        trigger={({ isOpen }) =>
          hasSplitButton ? (
            <IconButton
              name={isOpen ? 'caret-up' : 'caret-down'}
              className={tailwindOverride(
                'rounded-none	rounded-r h-8 w-8 flex items-center justify-center',
                {
                  'text-neutral-white bg-primary shadow-more-actions-button hover:bg-primary-darker hover:text-neutral-white focus-visible:bg-primary-darker focus-visible:text-neutral-white':
                    splitButtonVariant === 'primary',
                  'bg-primary-darker':
                    isOpen && splitButtonVariant === 'primary',
                },
              )}
              iconClassname='h-4 w-4'
              data-testid='more-actions-button-split'
            />
          ) : (
            <Button
              variant={buttonVariant}
              size='small'
              data-cy='more-actions-button'
              className={tailwindOverride({
                'bg-neutral-lightest': isOpen,
                'bg-primary-darker': buttonVariant === 'primary' && isOpen,
                'h-8': buttonVariant === 'primary',
              })}
            >
              <Typography
                className={tailwindOverride('text-primary mr-2 text-sm', {
                  'text-neutral-white': buttonVariant === 'primary',
                })}
              >
                {buttonText}
              </Typography>
              <Icon
                name={isOpen ? 'caret-up' : 'caret-down'}
                className='text-xs'
              />
            </Button>
          )
        }
      >
        {({ close }) => (
          <List
            options={options}
            data-cy='more-actions-list'
            renderOption={(option: MoreActionsOption) => (
              <ActionItem close={close} option={option} />
            )}
            onSelectOption={() => {}}
            className={listClassName}
          />
        )}
      </Popup>
    </div>
  );
};

export default MoreActionsPopup;
